/**
 * Stage layouts and tuning for the Donkey Kong-style prototype.
 *
 * Odd levels: solarpunk BUILD — climb to the top, building broken ladders
 * with W/C, while Kong-Bot rolls barrels; the energy reserve is the bonus
 * timer. Even levels: neon DEFEND — leeches drain your banked energy, sparks
 * chase you, ladders crack; survive the clock with energy left.
 * Each pair of stages is a loop; later loops are faster.
 */

// Girder surfaces (player feet), bottom tier 0 → top tier 5, logical px
export const TIER_Y_PX = [232, 196, 160, 124, 88, 52];

// id, from tier → to tier, x (px), rungs needed, rungs present
const BUILD_LADDERS = [
  { id: "a", from: 0, to: 1, x: 184, need: 6, rungs: 6 },
  { id: "b", from: 0, to: 1, x: 96, need: 6, rungs: 0 },
  { id: "c", from: 1, to: 2, x: 40, need: 6, rungs: 6 },
  { id: "d", from: 1, to: 2, x: 144, need: 6, rungs: 6 },
  { id: "e", from: 2, to: 3, x: 176, need: 8, rungs: 0 },
  { id: "f", from: 3, to: 4, x: 48, need: 6, rungs: 6 },
  { id: "g", from: 3, to: 4, x: 120, need: 6, rungs: 6 },
  { id: "h", from: 4, to: 5, x: 160, need: 10, rungs: 0 },
];

const TURBINES = [
  { id: 0, tier: 1, x: 112 },
  { id: 1, tier: 3, x: 88 },
  { id: 2, tier: 4, x: 150 }, // clear of where barrels drop from the top girder
];

export function stageKind(level) {
  return level % 2 === 1 ? "build" : "defend";
}

/** @returns {object} immutable layout + tuning for `level` */
export function getStageConfig(level) {
  const kind = stageKind(level);
  const loop = Math.floor((level - 1) / 2);
  const ladders =
    kind === "build"
      ? BUILD_LADDERS.map((l) => ({ ...l }))
      : BUILD_LADDERS.map((l) => ({ ...l, rungs: l.need }));

  return {
    level,
    kind,
    loop,
    tiersY: TIER_Y_PX,
    ladders,
    turbines: kind === "defend" ? TURBINES.map((t) => ({ ...t })) : [],
    start: { tier: 0, x: 24 },
    boss: { tier: 5, x: 24 },
    goalTier: 5,
    bite: kind === "build" ? { tier: 3, x: 176 } : { tier: 2, x: 112 },

    // Barrels (Kong-Bot at the top rolls them down)
    barrelSpawnX: 44,
    barrelEveryTicks: Math.max(60, (kind === "build" ? 125 : 150) - 15 * loop),
    barrelJitterTicks: 25,
    barrelSpeedSub: 24 + 2 * loop,
    ladderTakePct: 25,

    // Energy (integer Wh) — wood logs = Wh / whPerLog
    whPerLog: 10,
    reserveWh: kind === "build" ? 8000 : 0,
    reserveDrainWhPerSec: kind === "build" ? 100 : 0,
    defaultStartBankWh: kind === "build" ? 0 : 3000,
    turbineBonusWh: 500,
    jumpBonusWh: 100,
    bitePointsWh: [300, 500, 800],
    biteTicks: 500,

    // Hits
    lives: 3,
    invulnTicks: 50,
    hitPenaltyWh: 200, // defend stages
    stunTicks: 40,

    // Defend stage robots
    // One chasing spark on the first loop, a second from the next loop on
    sparks: kind === "defend" ? [{ tier: 2, x: 200 }, ...(loop >= 1 ? [{ tier: 4, x: 24 }] : [])] : [],
    sparkSpeedSub: 10 + loop, // half walking speed: outrun them, then they catch up
    leechDrainWhPerSec: 20,
    leechRespawnTicks: 400,
    pryTaps: 6,
    pryBonusWh: 200,
    crackEveryTicks: kind === "defend" ? 750 : 0,
    crackRungs: 3,
    crackLeakWhPerSec: 10,
    surviveTicks: 3000, // 60s
  };
}
